/* assets/sst-nclex-readiness-calculator.js */

(function() {
  'use strict';

  var REQUIRED_FIELDS = [
    'qbankScore',
    'assessmentScore',
    'qbankUsed',
    'qbankCompletionRange',
    'reviewProcess',
    'selfPacedVideos',
    'consistency',
    'liveCourseAttendance',
    'testTimeStamina',
    'tutoringHours',
    'confidence'
  ];

  var MAIN_WEIGHTS = {
    qbankScore: 0.20,
    reviewProcess: 0.18,
    assessmentScore: 0.14,
    qbankUsed: 0.06,
    strategyUse: 0.11,
    selfPacedVideos: 0.07,
    consistency: 0.05,
    liveCourseAttendance: 0.05,
    tutoringHours: 0.03,
    testTimeStamina: 0.07,
    confidence: 0.04
  };

  var CATEGORY_WEIGHTS = {
    adultHealth: 0.55,
    childHealth: 0.10,
    fundamentals: 0.08,
    leadership: 0.0675,
    maternal: 0.0675,
    pharmacology: 0.0675,
    mentalHealth: 0.0675
  };

  function Calculator(root) {
    this.root = root;
    this.bindEvents();
    this.toggleCategorySection();
    this.runStartupChecks();
  }

  Calculator.prototype.field = function(name) {
    return this.root.querySelector('[data-nclex-field="' + name + '"]');
  };

  Calculator.prototype.action = function(name) {
    return this.root.querySelector('[data-nclex-action="' + name + '"]');
  };

  Calculator.prototype.numberValue = function(name, required) {
    var element = this.field(name);
    if (!element) return required === false ? 0 : null;

    var value = parseFloat(String(element.value).replace(/[^0-9.]/g, ''));
    if (Number.isNaN(value)) return required === false ? 0 : null;

    return Math.max(0, Math.min(100, value));
  };

  Calculator.prototype.selectValue = function(name, required) {
    var element = this.field(name);
    if (!element) return required === false ? 0 : null;

    var value = parseFloat(element.value);
    if (Number.isNaN(value)) return required === false ? 0 : null;

    return value;
  };

  Calculator.prototype.textValue = function(name, fallback) {
    var element = this.field(name);
    var value = element && element.value ? element.value.trim() : '';
    return value || fallback || 'Not provided';
  };

  Calculator.prototype.isChecked = function(name) {
    var element = this.field(name);
    return !!(element && element.checked);
  };

  Calculator.prototype.strategyScore = function() {
    var names = ['drZMethod', 'askGraph', 'aash', 'nippaa', 'needss'];
    var checkedCount = names.filter(this.isChecked.bind(this)).length;
    return [25, 45, 60, 75, 90, 100][checkedCount];
  };

  Calculator.prototype.categoryFields = function() {
    if (!this.isChecked('enableCategoryBreakdown')) {
      return { categoryScore: null };
    }

    var fields = {
      adultHealth: this.numberValue('adultHealth'),
      childHealth: this.numberValue('childHealth'),
      fundamentals: this.numberValue('fundamentals'),
      leadership: this.numberValue('leadership'),
      maternal: this.numberValue('maternal'),
      pharmacology: this.numberValue('pharmacology'),
      mentalHealth: this.numberValue('mentalHealth')
    };

    var missing = Object.keys(fields).filter(function(key) {
      return fields[key] === null;
    });

    if (missing.length) {
      fields.categoryScore = null;
      fields.categoryMissing = missing;
      return fields;
    }

    var score = 0;
    Object.keys(CATEGORY_WEIGHTS).forEach(function(key) {
      score += fields[key] * CATEGORY_WEIGHTS[key];
    });

    fields.categoryScore = Math.round(score);
    fields.categoryMissing = [];
    return fields;
  };

  Calculator.prototype.fields = function() {
    var fields = {
      qbankScore: this.numberValue('qbankScore'),
      assessmentScore: this.numberValue('assessmentScore'),
      qbankUsed: this.selectValue('qbankUsed'),
      qbankCompletionRange: this.textValue('qbankCompletionRange', ''),
      reviewProcess: this.selectValue('reviewProcess'),
      strategyUse: this.strategyScore(),
      selfPacedVideos: this.selectValue('selfPacedVideos'),
      consistency: this.selectValue('consistency'),
      liveCourseAttendance: this.selectValue('liveCourseAttendance'),
      tutoringHours: this.selectValue('tutoringHours'),
      testTimeStamina: this.selectValue('testTimeStamina'),
      confidence: this.selectValue('confidence')
    };

    var categories = this.categoryFields();
    Object.keys(categories).forEach(function(key) {
      fields[key] = categories[key];
    });

    return fields;
  };

  Calculator.prototype.validate = function(fields) {
    var missing = REQUIRED_FIELDS.filter(function(key) {
      return fields[key] === null || fields[key] === '';
    });

    if (missing.length) {
      window.alert('Please complete all mandatory readiness parameters before calculating.');
      return false;
    }

    if (this.isChecked('enableCategoryBreakdown') && fields.categoryMissing && fields.categoryMissing.length) {
      window.alert('Please complete all optional category breakdown fields or turn off the optional section.');
      return false;
    }

    return true;
  };

  Calculator.prototype.mainScore = function(fields) {
    var total = 0;
    Object.keys(MAIN_WEIGHTS).forEach(function(key) {
      total += fields[key] * MAIN_WEIGHTS[key];
    });
    return Math.round(total);
  };

  Calculator.prototype.readinessOverride = function(fields, score) {
    if (fields.qbankCompletionRange === 'low') {
      return {
        label: 'Not Ready',
        modifier: 'not-ready',
        message: 'This student is not ready yet because they have completed 0-50% of the qbank. Before testing, they need more question exposure, more content review, and more rationale work. At this stage, the score is not reliable enough to greenlight readiness.'
      };
    }

    if (fields.qbankCompletionRange === 'complete' && fields.qbankScore >= 75 && fields.reviewProcess >= 80 && fields.testTimeStamina >= 90) {
      return {
        label: 'Strong Readiness',
        modifier: 'ready',
        message: 'This student is showing strong readiness because they completed 100% of the qbank, have an average score of at least 75%, consistently reviewed rationales and completed incorrect answers, and can study 4-5+ consecutive hours at their test time. Assuming the scores are not memorized, this is a strong readiness pattern.'
      };
    }

    if (fields.qbankCompletionRange === 'complete' && (fields.reviewProcess < 80 || fields.testTimeStamina < 90)) {
      return {
        label: score >= 75 ? 'Almost Ready' : 'Caution Zone',
        modifier: score >= 75 ? 'almost' : 'caution',
        message: 'This student has completed the qbank, but their rationale review/incorrect remediation process or test-time stamina is still not strong enough. They should not be considered Strong Readiness until incorrects are reviewed and they can handle 4-5+ consecutive study hours at the same time of day as their exam.'
      };
    }

    if (fields.qbankCompletionRange === 'moderate' && fields.reviewProcess >= 80 && fields.testTimeStamina >= 70 && score >= 75) {
      return {
        label: 'Almost Ready',
        modifier: 'almost',
        message: 'This student is almost ready because they completed 50-80% of the qbank, reviewed their incorrect answers, and have at least moderate test-time stamina. They are moving in the right direction, but they should keep building question volume before being considered strongly ready.'
      };
    }

    return null;
  };

  Calculator.prototype.calculate = function() {
    var fields = this.fields();
    if (!this.validate(fields)) return;

    var score = this.mainScore(fields);
    this.showResult(score, fields, this.readinessOverride(fields, score));
  };

  Calculator.prototype.resultForScore = function(score, override) {
    if (override) return override;

    if (score < 60) {
      return {
        label: 'Not Ready',
        modifier: 'not-ready',
        message: 'This student likely needs more content review, strategy work, and qbank practice before testing. The focus should be strengthening content foundations, completing more questions, and reviewing rationales deeply.'
      };
    }

    if (score < 75) {
      return {
        label: 'Caution Zone',
        modifier: 'caution',
        message: 'This student is improving, but testing now may still be risky. They should focus on consistency, content reinforcement, priority questions, and understanding why each answer is right or wrong.'
      };
    }

    if (score < 85) {
      return {
        label: 'Almost Ready',
        modifier: 'almost',
        message: 'This student is close. Before testing, they should make sure scores are consistent, not memorized, and that they are applying strategy across Adult Health and client management questions.'
      };
    }

    return {
      label: 'Strong Readiness',
      modifier: 'ready',
      message: 'This student is showing strong readiness signs. If their performance is consistent and not based on memorization, they may be in a good position to consider testing.'
    };
  };

  Calculator.prototype.showResult = function(score, fields, override) {
    var result = this.field('result');
    var scoreValue = this.field('scoreValue');
    var badge = this.field('badge');
    var recommendation = this.field('recommendation');
    var resultData = this.resultForScore(score, override);

    if (!result || !scoreValue || !badge || !recommendation) {
      window.alert('The result section is missing from the page. Please check the calculator markup.');
      return;
    }

    scoreValue.textContent = score;
    badge.textContent = resultData.label;
    badge.className = 'sst-nclex-calculator__badge sst-nclex-calculator__badge--' + resultData.modifier;
    recommendation.textContent = resultData.message;
    result.classList.remove('is-hidden');

    this.createSummary(score, resultData.label, resultData.message, fields);
    result.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  Calculator.prototype.formatQbankCompletion = function(value) {
    if (value === 'low') return '0-50% completed';
    if (value === 'moderate') return '50-80% completed';
    if (value === 'complete') return '100% completed';
    return 'Not provided';
  };

  Calculator.prototype.createSummary = function(score, label, message, fields) {
    var categoryText = fields.categoryScore === null
      ? 'Optional category breakdown not included.'
      : [
        'Optional Category Breakdown Score: ' + fields.categoryScore + '/100',
        'Adult Health %: ' + fields.adultHealth + '%',
        'Child Health %: ' + fields.childHealth + '%',
        'Fundamentals %: ' + fields.fundamentals + '%',
        'Leadership and Management %: ' + fields.leadership + '%',
        'Maternal and Newborn Health %: ' + fields.maternal + '%',
        'Pharmacology %: ' + fields.pharmacology + '%',
        'Mental Health %: ' + fields.mentalHealth + '%'
      ].join('\n');

    var summary = [
      "Dr. Zeeshan's NCLEX Readiness Calculator Summary",
      '',
      'Student: ' + this.textValue('name'),
      'Email: ' + this.textValue('email'),
      'Test Date: ' + this.textValue('testDate'),
      'Attempts: ' + this.textValue('attempts'),
      '',
      'Readiness Score: ' + score + '/100',
      'Readiness Category: ' + label,
      '',
      'Mandatory Parameters:',
      'QBank Average: ' + fields.qbankScore + '%',
      'Assessment Average: ' + fields.assessmentScore + '%',
      'Primary QBank Used Score: ' + fields.qbankUsed + '/100',
      'QBank Completion Range: ' + this.formatQbankCompletion(fields.qbankCompletionRange),
      'Rationale + Incorrect Questions Review Score: ' + fields.reviewProcess + '/100',
      'NCLEX High Yield Strategies Score: ' + fields.strategyUse + '/100',
      'Self-Paced Online Videos Score: ' + fields.selfPacedVideos + '/100',
      'Consistency Score: ' + fields.consistency + '/100',
      'NHY Live Course Attendance Score: ' + fields.liveCourseAttendance + '/100',
      'Consecutive Study Hours at Test Time Score: ' + fields.testTimeStamina + '/100',
      '1-on-1 Tutoring Hours Score: ' + fields.tutoringHours + '/100',
      'Confidence Score: ' + fields.confidence + '/100',
      '',
      categoryText,
      '',
      'Recommendation:',
      message,
      '',
      'Disclaimer: This calculator is for educational guidance only and does not guarantee NCLEX results.'
    ].join('\n');

    var summaryText = this.field('summaryText');
    if (summaryText) summaryText.value = summary;
  };

  Calculator.prototype.copySummary = function() {
    var summaryText = this.field('summaryText');
    if (!summaryText || !summaryText.value) {
      window.alert('Please calculate readiness first.');
      return;
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(summaryText.value).then(function() {
        window.alert('Summary copied.');
      }).catch(function() {
        summaryText.select();
        document.execCommand('copy');
        window.alert('Summary copied.');
      });
      return;
    }

    summaryText.select();
    document.execCommand('copy');
    window.alert('Summary copied.');
  };

  Calculator.prototype.escapeHtml = function(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  Calculator.prototype.downloadReport = function() {
    var summaryText = this.field('summaryText');
    if (!summaryText || !summaryText.value) {
      window.alert('Please calculate readiness before downloading the report.');
      return;
    }

    var score = this.escapeHtml(this.field('scoreValue') ? this.field('scoreValue').textContent : '0');
    var readiness = this.escapeHtml(this.field('badge') ? this.field('badge').textContent : 'Result');
    var recommendation = this.escapeHtml(this.field('recommendation') ? this.field('recommendation').textContent : '');
    var escapedSummary = this.escapeHtml(summaryText.value);
    var detailedPlan = [
      'NEXT STEPS RECOMMENDATION PLAN',
      '',
      '1. Continue answering NCLEX-style questions daily with strong focus on understanding why each answer is correct or incorrect.',
      '',
      '2. Prioritize deep rationale review instead of memorization. Students who only memorize scores often struggle on real NCLEX questions.',
      '',
      '3. Focus heavily on Adult Health, prioritization, delegation, and client management because these areas commonly drive NCLEX readiness.',
      '',
      '4. Build test-day stamina by studying at the same time as your scheduled NCLEX exam for at least 4-5 consecutive hours.',
      '',
      "5. Continue reinforcing NCLEX High Yield strategies including Dr. Zeeshan's Method, ASK GRAPH, AASH, NIPPAA, and NEEDSS.",
      '',
      '6. Continue reviewing self-paced videos, attending live courses, and using tutoring support if needed.',
      '',
      '7. Do not rush your test date if your scores are inconsistent or based on memorization rather than true understanding.'
    ].join('\n');

    var reportWindow = window.open('', '_blank');
    if (!reportWindow) {
      window.alert('Pop-up blocked. Please allow pop-ups to download or print the PDF report.');
      return;
    }

    reportWindow.document.write(
      '<html><head><title>Dr. Zeeshan\'s NCLEX Readiness Report</title>' +
      '<style>' +
      'body{font-family:Arial,sans-serif;padding:0;margin:0;color:#111827;background:#eef4f3;line-height:1.6;}' +
      '.container{max-width:980px;margin:0 auto;background:#fff;min-height:100vh;}' +
      '.header{background:linear-gradient(135deg,#0f766e,#134e4a);color:#fff;padding:50px 45px;}' +
      '.header h1{margin:0;font-size:34px;letter-spacing:0;line-height:1.2;}.header p{margin-top:10px;opacity:.9;font-size:16px;}' +
      '.content{padding:35px 45px 50px;}.top-grid{display:grid;grid-template-columns:1.1fr 1fr;gap:24px;margin-bottom:28px;}' +
      '.score-card{background:linear-gradient(135deg,#ecfeff,#f0fdfa);border:2px solid #99f6e4;border-radius:22px;padding:28px;}' +
      '.score-label{font-size:13px;text-transform:uppercase;color:#0f766e;font-weight:700;letter-spacing:1px;}' +
      '.score{font-size:72px;font-weight:800;color:#0f766e;line-height:1;margin:10px 0;}' +
      '.badge{display:inline-block;padding:10px 16px;border-radius:999px;font-weight:700;font-size:14px;margin-top:6px;background:#d1fae5;color:#065f46;}' +
      '.recommendation-card{background:#f8fafc;border:1px solid #e2e8f0;border-radius:22px;padding:28px;}' +
      '.metrics-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-top:24px;}' +
      '.metric-card{background:#fff;border:1px solid #e5e7eb;border-radius:16px;padding:18px;text-align:center;box-shadow:0 2px 8px rgba(15,23,42,.04);}' +
      '.metric-card h3{margin:0;font-size:13px;color:#6b7280;font-weight:600;}.metric-value{margin-top:10px;font-size:28px;font-weight:800;color:#0f766e;}' +
      '.section-title{font-size:22px;margin-top:38px;margin-bottom:16px;color:#0f172a;}.summary-box,.next-steps{background:#f8fafc;border:1px solid #e5e7eb;border-radius:18px;padding:24px;}' +
      'pre{white-space:pre-wrap;font-family:Arial,sans-serif;margin:0;}.footer{margin-top:36px;padding-top:20px;border-top:1px solid #e5e7eb;color:#6b7280;font-size:12px;}' +
      '@media(max-width:760px){.top-grid,.metrics-grid{grid-template-columns:1fr;}.header,.content{padding-left:24px;padding-right:24px;}}@media print{body{background:#fff}.container{max-width:100%;}}' +
      '</style></head><body>' +
      '<div class="container"><div class="header"><h1>Dr. Zeeshan\'s NCLEX Readiness Report</h1><p>Personalized NCLEX readiness analysis, performance review, and guided next steps.</p></div>' +
      '<div class="content"><div class="top-grid"><div class="score-card"><div class="score-label">Overall Readiness Score</div><div class="score">' + score + '/100</div><div class="badge">' + readiness + '</div></div>' +
      '<div class="recommendation-card"><h3 style="margin-top:0;color:#0f172a;">Professional Readiness Recommendation</h3><p style="margin-bottom:0;">' + recommendation + '</p></div></div>' +
      '<div class="metrics-grid"><div class="metric-card"><h3>QBank Goal</h3><div class="metric-value">75%+</div></div><div class="metric-card"><h3>Readiness Focus</h3><div class="metric-value">Consistency</div></div><div class="metric-card"><h3>Study Endurance Goal</h3><div class="metric-value">5h+</div></div></div>' +
      '<h2 class="section-title">Student Readiness Summary</h2><div class="summary-box"><pre>' + escapedSummary + '</pre></div>' +
      '<h2 class="section-title">Detailed Next Steps</h2><div class="next-steps"><pre>' + this.escapeHtml(detailedPlan) + '</pre></div>' +
      '<div class="footer">Disclaimer: This readiness calculator is for educational guidance only and does not guarantee NCLEX results. Final testing decisions remain the responsibility of the student.</div>' +
      '</div></div><script>window.onload=function(){window.print();};<\/script></body></html>'
    );
    reportWindow.document.close();
  };

  Calculator.prototype.reset = function() {
    this.root.querySelectorAll('input, select').forEach(function(element) {
      if (element.type === 'checkbox') {
        element.checked = false;
      } else {
        element.value = '';
      }
    });

    this.toggleCategorySection();

    var result = this.field('result');
    var summaryText = this.field('summaryText');
    if (result) result.classList.add('is-hidden');
    if (summaryText) summaryText.value = '';
  };

  Calculator.prototype.toggleCategorySection = function() {
    var content = this.field('categoryContent');
    if (!content) return;
    content.classList.toggle('is-disabled', !this.isChecked('enableCategoryBreakdown'));
  };

  Calculator.prototype.runTests = function() {
    var weightTotal = Object.keys(MAIN_WEIGHTS).reduce(function(sum, key) {
      return sum + MAIN_WEIGHTS[key];
    }, 0);

    console.assert(Math.abs(weightTotal - 1) < 0.0001, 'MAIN_WEIGHTS should equal 1. Current total: ' + weightTotal);
    console.assert(this.strategyScore() >= 25, 'Strategy score should have a minimum value.');
    console.assert(typeof this.fields() === 'object', 'fields should return an object.');

    REQUIRED_FIELDS.forEach(function(name) {
      console.assert(!!this.field(name), 'Required field should exist: ' + name);
    }, this);

    window.alert('Built-in tests completed. Open the browser console for details.');
  };

  Calculator.prototype.bindEvents = function() {
    var calculate = this.action('calculate');
    var reset = this.action('reset');
    var tests = this.action('tests');
    var copy = this.action('copy');
    var download = this.action('download');
    var optionalToggle = this.field('enableCategoryBreakdown');

    if (calculate) calculate.addEventListener('click', this.calculate.bind(this));
    if (reset) reset.addEventListener('click', this.reset.bind(this));
    if (tests) tests.addEventListener('click', this.runTests.bind(this));
    if (copy) copy.addEventListener('click', this.copySummary.bind(this));
    if (download) download.addEventListener('click', this.downloadReport.bind(this));
    if (optionalToggle) optionalToggle.addEventListener('change', this.toggleCategorySection.bind(this));

    this.root.querySelectorAll('[data-nclex-numeric]').forEach(function(element) {
      element.addEventListener('input', function() {
        var cleaned = this.value.replace(/[^0-9.]/g, '');
        var firstDot = cleaned.indexOf('.');

        if (firstDot !== -1) {
          cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
        }

        if (this.value !== cleaned) this.value = cleaned;
      });
    });
  };

  Calculator.prototype.runStartupChecks = function() {
    REQUIRED_FIELDS.forEach(function(name) {
      if (!this.field(name)) console.error('Startup check failed. Missing required field: ' + name);
    }, this);
  };

  function initCalculator(root) {
    if (!root || root.dataset.nclexInitialized === 'true') return;
    root.dataset.nclexInitialized = 'true';
    new Calculator(root);
  }

  function initAll(scope) {
    var base = scope || document;
    base.querySelectorAll('[data-nclex-calculator]').forEach(initCalculator);
  }

  document.addEventListener('DOMContentLoaded', function() {
    initAll(document);
  });

  document.addEventListener('shopify:section:load', function(event) {
    initAll(event.target);
  });
})();
